import React, { useEffect, useState } from 'react';
import { fmtDateTime } from '../utils/dateUtils';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';
import useIsMobile from '../hooks/useIsMobile';

const defaultHappyHourForm = {
  menu_item_id: '',
  day_of_week: '',
  time_from: '16:00',
  time_to: '19:00',
  profile_price: '',
  is_active: true
};

const defaultBogoForm = {
  rule_name: '',
  buy_menu_item_id: '',
  buy_qty: '1',
  free_menu_item_id: '',
  free_qty: '1',
  day_of_week: '',
  time_from: '',
  time_to: '',
  is_active: true
};

function BarPromotionsMaster({ token, selectedPropertyId }) {
  const { isMobile } = useIsMobile();
  const [menuItems, setMenuItems] = useState([]);
  const [happyHourProfiles, setHappyHourProfiles] = useState([]);
  const [bogoRules, setBogoRules] = useState([]);

  const [happyHourForm, setHappyHourForm] = useState(defaultHappyHourForm);
  const [bogoForm, setBogoForm] = useState(defaultBogoForm);

  const [editingHappyHourId, setEditingHappyHourId] = useState('');
  const [editingBogoId, setEditingBogoId] = useState('');

  const [happyHourBusy, setHappyHourBusy] = useState(false);
  const [bogoBusy, setBogoBusy] = useState(false);
  const [happyHourError, setHappyHourError] = useState('');
  const [bogoError, setBogoError] = useState('');
  const happyHour = useEditFormScroll();
  const bogo = useEditFormScroll();

  const chainId = localStorage.getItem('hotelConfigSelectedChainId') || '';
  const propertyId = selectedPropertyId || localStorage.getItem('selectedPropertyId') || '';

  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  useEffect(() => {
    loadMenuItems();
    loadHappyHourProfiles();
    loadBogoRules();
  }, [token, propertyId]);

  const loadMenuItems = () => {
    fetch('/api/menu-setup', { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => {
        const rows = Array.isArray(data) ? data : (data && Array.isArray(data.items) ? data.items : []);
        setMenuItems(rows);
      })
      .catch(() => setMenuItems([]));
  };

  const loadHappyHourProfiles = () => {
    if (!propertyId) {
      setHappyHourProfiles([]);
      return;
    }
    fetch('/api/bar/promotions/price-profiles', { headers: authHeaders() })
      .then(async (res) => {
        const data = await res.json().catch(() => ([]));
        if (!res.ok) {
          setHappyHourProfiles([]);
          if (res.status !== 403) setHappyHourError(data?.error || 'Failed to load Happy Hour profiles');
          return;
        }
        setHappyHourError('');
        setHappyHourProfiles(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setHappyHourProfiles([]);
        setHappyHourError('Failed to load Happy Hour profiles');
      });
  };

  const loadBogoRules = () => {
    if (!propertyId) {
      setBogoRules([]);
      return;
    }
    fetch('/api/bar/promotions/bogo', { headers: authHeaders() })
      .then(async (res) => {
        const data = await res.json().catch(() => ([]));
        if (!res.ok) {
          setBogoRules([]);
          if (res.status !== 403) setBogoError(data?.error || 'Failed to load BOGO rules');
          return;
        }
        setBogoError('');
        setBogoRules(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setBogoRules([]);
        setBogoError('Failed to load BOGO rules');
      });
  };

  const normalizeTime = (value) => String(value || '').slice(0, 5);

  const resetHappyHourForm = () => {
    setEditingHappyHourId('');
    setHappyHourForm(defaultHappyHourForm);
  };

  const resetBogoForm = () => {
    setEditingBogoId('');
    setBogoForm(defaultBogoForm);
  };

  const startEditHappyHour = (row) => {
    setEditingHappyHourId(String(row.id));
    setHappyHourError('');
    setHappyHourForm({
      menu_item_id: String(row.menu_item_id || ''),
      day_of_week: row.day_of_week == null ? '' : String(row.day_of_week),
      time_from: normalizeTime(row.time_from) || '16:00',
      time_to: normalizeTime(row.time_to) || '19:00',
      profile_price: String(row.profile_price ?? ''),
      is_active: row.is_active !== false
    });
    happyHour.scrollToForm();
  };

  const startEditBogo = (row) => {
    setEditingBogoId(String(row.id));
    setBogoError('');
    setBogoForm({
      rule_name: String(row.rule_name || ''),
      buy_menu_item_id: String(row.buy_menu_item_id || ''),
      buy_qty: String(row.buy_qty || 1),
      free_menu_item_id: String(row.free_menu_item_id || ''),
      free_qty: String(row.free_qty || 1),
      day_of_week: row.day_of_week == null ? '' : String(row.day_of_week),
      time_from: normalizeTime(row.time_from),
      time_to: normalizeTime(row.time_to),
      is_active: row.is_active !== false
    });
    bogo.scrollToForm();
  };

  const createOrUpdateHappyHour = async () => {
    const menuItemId = Number(happyHourForm.menu_item_id || 0);
    const profilePrice = Number(happyHourForm.profile_price || 0);
    const timeFrom = String(happyHourForm.time_from || '').trim();
    const timeTo = String(happyHourForm.time_to || '').trim();
    const dayRaw = String(happyHourForm.day_of_week || '').trim();

    if (!menuItemId) return setHappyHourError('Select menu item for Happy Hour');
    if (!timeFrom || !timeTo) return setHappyHourError('Set time window');
    if (!Number.isFinite(profilePrice) || profilePrice < 0) return setHappyHourError('Enter valid Happy Hour price');

    setHappyHourBusy(true);
    try {
      const payload = {
        menu_item_id: menuItemId,
        profile_type: 'HAPPY_HOUR',
        day_of_week: dayRaw === '' ? null : Number(dayRaw),
        time_from: timeFrom,
        time_to: timeTo,
        profile_price: profilePrice,
        is_active: happyHourForm.is_active === true
      };

      const isEdit = Boolean(editingHappyHourId);
      const url = isEdit
        ? `/api/bar/promotions/price-profiles/${encodeURIComponent(editingHappyHourId)}`
        : '/api/bar/promotions/price-profiles';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setHappyHourError(data?.error || `Failed to ${isEdit ? 'update' : 'save'} Happy Hour profile`);
        return;
      }

      setHappyHourError('');
      resetHappyHourForm();
      await Promise.all([loadHappyHourProfiles(), loadMenuItems()]);
    } catch {
      setHappyHourError(`Network error while ${editingHappyHourId ? 'updating' : 'saving'} Happy Hour profile`);
    } finally {
      setHappyHourBusy(false);
    }
  };

  const createOrUpdateBogoRule = async () => {
    const ruleName = String(bogoForm.rule_name || '').trim();
    const buyMenuItemId = Number(bogoForm.buy_menu_item_id || 0);
    const freeMenuItemId = Number(bogoForm.free_menu_item_id || bogoForm.buy_menu_item_id || 0);
    const buyQty = Number(bogoForm.buy_qty || 0);
    const freeQty = Number(bogoForm.free_qty || 0);
    const dayRaw = String(bogoForm.day_of_week || '').trim();
    const timeFrom = String(bogoForm.time_from || '').trim();
    const timeTo = String(bogoForm.time_to || '').trim();

    if (!ruleName) return setBogoError('Rule name is required');
    if (!buyMenuItemId) return setBogoError('Select buy menu item');
    if (!freeMenuItemId) return setBogoError('Select free menu item');
    if (!Number.isInteger(buyQty) || buyQty <= 0) return setBogoError('Buy qty must be greater than 0');
    if (!Number.isInteger(freeQty) || freeQty <= 0) return setBogoError('Free qty must be greater than 0');

    setBogoBusy(true);
    try {
      const payload = {
        rule_name: ruleName,
        buy_menu_item_id: buyMenuItemId,
        buy_qty: buyQty,
        free_menu_item_id: freeMenuItemId,
        free_qty: freeQty,
        day_of_week: dayRaw === '' ? null : Number(dayRaw),
        time_from: timeFrom || null,
        time_to: timeTo || null,
        is_active: bogoForm.is_active === true
      };

      const isEdit = Boolean(editingBogoId);
      const url = isEdit
        ? `/api/bar/promotions/bogo/${encodeURIComponent(editingBogoId)}`
        : '/api/bar/promotions/bogo';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setBogoError(data?.error || `Failed to ${isEdit ? 'update' : 'save'} BOGO rule`);
        return;
      }

      setBogoError('');
      resetBogoForm();
      await loadBogoRules();
    } catch {
      setBogoError(`Network error while ${editingBogoId ? 'updating' : 'saving'} BOGO rule`);
    } finally {
      setBogoBusy(false);
    }
  };

  const updateHappyHourStatus = async (row, nextActive) => {
    const payload = {
      menu_item_id: Number(row.menu_item_id || 0),
      profile_type: String(row.profile_type || 'HAPPY_HOUR').toUpperCase(),
      day_of_week: row.day_of_week == null ? null : Number(row.day_of_week),
      time_from: normalizeTime(row.time_from),
      time_to: normalizeTime(row.time_to),
      profile_price: Number(row.profile_price || 0),
      is_active: nextActive === true
    };
    try {
      const res = await fetch(`/api/bar/promotions/price-profiles/${encodeURIComponent(String(row.id))}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setHappyHourError(data?.error || 'Failed to update Happy Hour status');
      await loadHappyHourProfiles();
      setHappyHourError('');
    } catch {
      setHappyHourError('Network error while updating Happy Hour status');
    }
  };

  const updateBogoStatus = async (row, nextActive) => {
    const payload = {
      rule_name: String(row.rule_name || '').trim(),
      buy_menu_item_id: Number(row.buy_menu_item_id || 0),
      buy_qty: Number(row.buy_qty || 0),
      free_menu_item_id: Number(row.free_menu_item_id || 0),
      free_qty: Number(row.free_qty || 0),
      day_of_week: row.day_of_week == null ? null : Number(row.day_of_week),
      time_from: normalizeTime(row.time_from) || null,
      time_to: normalizeTime(row.time_to) || null,
      is_active: nextActive === true
    };
    try {
      const res = await fetch(`/api/bar/promotions/bogo/${encodeURIComponent(String(row.id))}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setBogoError(data?.error || 'Failed to update BOGO status');
      await loadBogoRules();
      setBogoError('');
    } catch {
      setBogoError('Network error while updating BOGO status');
    }
  };

  const dayLabel = (day) => {
    if (day === null || day === undefined || String(day).trim() === '') return 'All Days';
    const index = Number(day);
    if (!Number.isInteger(index) || index < 0 || index > 6) return 'All Days';
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][index];
  };

  const formatTimestamp = (value) => {
    if (!value) return '-';
    return fmtDateTime(value);
  };

  const formatUserLabel = (username, userId) => {
    const name = String(username || '').trim();
    if (name) return name;
    if (userId !== undefined && userId !== null && String(userId).trim() !== '') return `User #${userId}`;
    return '-';
  };

  const bogoTimeWindow = (row) => {
    const fromText = normalizeTime(row?.time_from);
    const toText = normalizeTime(row?.time_to);
    if (fromText && toText) return `${fromText} - ${toText}`;
    return 'All Time';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      <div className="card" ref={happyHour.formRef} style={editHighlightStyle(happyHour.editHighlight)}>
        <div className="card-body">
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', marginBottom: '0.6rem' }}>Promotions - Happy Hour</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '1.2fr 0.8fr 0.7fr 0.7fr 0.8fr auto auto', gap: '0.45rem', marginBottom: '0.55rem' }}>
            <select
              value={happyHourForm.menu_item_id}
              onChange={(e) => setHappyHourForm((prev) => ({ ...prev, menu_item_id: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            >
              <option value="">Select Item</option>
              {menuItems.map((item) => (
                <option key={item.id} value={item.id}>{item.menu_item_name}</option>
              ))}
            </select>
            <select
              value={happyHourForm.day_of_week}
              onChange={(e) => setHappyHourForm((prev) => ({ ...prev, day_of_week: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            >
              <option value="">All Days</option>
              <option value="0">Sunday</option>
              <option value="1">Monday</option>
              <option value="2">Tuesday</option>
              <option value="3">Wednesday</option>
              <option value="4">Thursday</option>
              <option value="5">Friday</option>
              <option value="6">Saturday</option>
            </select>
            <input
              type="time"
              value={happyHourForm.time_from}
              onChange={(e) => setHappyHourForm((prev) => ({ ...prev, time_from: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            />
            <input
              type="time"
              value={happyHourForm.time_to}
              onChange={(e) => setHappyHourForm((prev) => ({ ...prev, time_to: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            />
            <input
              type="number"
              min="0"
              step="0.01"
              value={happyHourForm.profile_price}
              placeholder="HH Price"
              onChange={(e) => setHappyHourForm((prev) => ({ ...prev, profile_price: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            />
            <button
              type="button"
              onClick={createOrUpdateHappyHour}
              disabled={happyHourBusy}
              className="btn-primary"
              style={{ border: 'none', borderRadius: '8px', padding: '0.46rem 0.75rem', cursor: happyHourBusy ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.78rem', minHeight: '38px', opacity: happyHourBusy ? 0.6 : 1 }}
            >
              {happyHourBusy ? (editingHappyHourId ? 'Updating...' : 'Saving...') : (editingHappyHourId ? 'Update' : 'Add')}
            </button>
            <button
              type="button"
              onClick={resetHappyHourForm}
              disabled={happyHourBusy || !editingHappyHourId}
              style={{ border: '1px solid var(--theme-border)', borderRadius: '8px', background: '#fff', color: '#475569', padding: '0.46rem 0.75rem', cursor: (happyHourBusy || !editingHappyHourId) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.78rem', minHeight: '38px' }}
            >
              Cancel
            </button>
          </div>
          {happyHourError && <div className="error-banner">{happyHourError}</div>}
          <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--theme-border)', borderRadius: '8px' }}>
            <div style={{ padding: '0.52rem 0.75rem', borderBottom: '1px solid var(--theme-border)', fontSize: '0.73rem', fontWeight: 700, color: '#475569', background: 'var(--theme-bg-light)', display: 'grid', gridTemplateColumns: '1.4fr 0.9fr 1.3fr', gap: '0.4rem' }}>
              <div>Rule / Window</div>
              <div>Price</div>
              <div style={{ textAlign: 'right' }}>Status / Actions</div>
            </div>
            {!happyHourProfiles.length ? (
              <div style={{ padding: '0.65rem 0.75rem', color: 'var(--theme-text-muted)', fontSize: '0.78rem' }}>No Happy Hour profiles found</div>
            ) : happyHourProfiles.map((row) => (
              <div key={row.id} style={{ padding: '0.58rem 0.75rem', borderBottom: '1px solid var(--theme-border-light)', fontSize: '0.78rem', color: '#334155', display: 'grid', gridTemplateColumns: '1.4fr 0.9fr 1.3fr', gap: '0.4rem' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{row.menu_item_name || `Item #${row.menu_item_id}`}</div>
                  <div style={{ color: 'var(--theme-text-secondary)' }}>{dayLabel(row.day_of_week)} · {normalizeTime(row.time_from)} - {normalizeTime(row.time_to)}</div>
                  <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.7rem' }}>Created: {formatTimestamp(row.created_at)} by {formatUserLabel(row.created_by_username, row.created_by)}</div>
                  <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.7rem' }}>Modified: {formatTimestamp(row.updated_at || row.created_at)} by {formatUserLabel(row.updated_by_username, row.updated_by)}</div>
                </div>
                <div style={{ fontWeight: 700, color: '#0f766e' }}>₹{Number(row.profile_price || 0).toFixed(2)}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', justifyContent: 'flex-end' }}>
                  <span style={{ color: row.is_active === false ? '#b91c1c' : '#15803d', fontWeight: 600 }}>{row.is_active === false ? 'INACTIVE' : 'ACTIVE'}</span>
                  <button type="button" onClick={() => startEditHappyHour(row)} style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#1e40af', borderRadius: '7px', padding: '0.26rem 0.5rem', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                  <button
                    type="button"
                    onClick={() => updateHappyHourStatus(row, row.is_active === false)}
                    style={{ border: '1px solid #cbd5e1', background: '#fff', color: row.is_active === false ? '#15803d' : '#b91c1c', borderRadius: '7px', padding: '0.26rem 0.5rem', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {row.is_active === false ? 'Activate' : 'Deactivate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" ref={bogo.formRef} style={editHighlightStyle(bogo.editHighlight)}>
        <div className="card-body">
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', marginBottom: '0.6rem' }}>Promotions - BOGO</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '1fr 1fr 0.45fr 1fr 0.45fr 0.9fr 0.7fr 0.7fr auto auto', gap: '0.45rem', marginBottom: '0.55rem' }}>
            <input
              value={bogoForm.rule_name}
              placeholder="Rule Name"
              onChange={(e) => setBogoForm((prev) => ({ ...prev, rule_name: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            />
            <select
              value={bogoForm.buy_menu_item_id}
              onChange={(e) => setBogoForm((prev) => ({
                ...prev,
                buy_menu_item_id: e.target.value,
                free_menu_item_id: prev.free_menu_item_id || e.target.value
              }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            >
              <option value="">Buy Item</option>
              {menuItems.map((item) => (
                <option key={item.id} value={item.id}>{item.menu_item_name}</option>
              ))}
            </select>
            <input
              type="number"
              min="1"
              step="1"
              value={bogoForm.buy_qty}
              placeholder="Buy"
              onChange={(e) => setBogoForm((prev) => ({ ...prev, buy_qty: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            />
            <select
              value={bogoForm.free_menu_item_id}
              onChange={(e) => setBogoForm((prev) => ({ ...prev, free_menu_item_id: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            >
              <option value="">Free Item</option>
              {menuItems.map((item) => (
                <option key={item.id} value={item.id}>{item.menu_item_name}</option>
              ))}
            </select>
            <input
              type="number"
              min="1"
              step="1"
              value={bogoForm.free_qty}
              placeholder="Free"
              onChange={(e) => setBogoForm((prev) => ({ ...prev, free_qty: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            />
            <select
              value={bogoForm.day_of_week}
              onChange={(e) => setBogoForm((prev) => ({ ...prev, day_of_week: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            >
              <option value="">All Days</option>
              <option value="0">Sunday</option>
              <option value="1">Monday</option>
              <option value="2">Tuesday</option>
              <option value="3">Wednesday</option>
              <option value="4">Thursday</option>
              <option value="5">Friday</option>
              <option value="6">Saturday</option>
            </select>
            <input
              type="time"
              value={bogoForm.time_from}
              onChange={(e) => setBogoForm((prev) => ({ ...prev, time_from: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            />
            <input
              type="time"
              value={bogoForm.time_to}
              onChange={(e) => setBogoForm((prev) => ({ ...prev, time_to: e.target.value }))}
              style={{ padding: '0.46rem 0.55rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.8rem', minHeight: '38px' }}
            />
            <button
              type="button"
              onClick={createOrUpdateBogoRule}
              disabled={bogoBusy}
              className="btn-primary"
              style={{ border: 'none', borderRadius: '8px', padding: '0.46rem 0.75rem', cursor: bogoBusy ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.78rem', minHeight: '38px', opacity: bogoBusy ? 0.6 : 1 }}
            >
              {bogoBusy ? (editingBogoId ? 'Updating...' : 'Saving...') : (editingBogoId ? 'Update' : 'Add')}
            </button>
            <button
              type="button"
              onClick={resetBogoForm}
              disabled={bogoBusy || !editingBogoId}
              style={{ border: '1px solid var(--theme-border)', borderRadius: '8px', background: '#fff', color: '#475569', padding: '0.46rem 0.75rem', cursor: (bogoBusy || !editingBogoId) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.78rem', minHeight: '38px' }}
            >
              Cancel
            </button>
          </div>
          {bogoError && <div className="error-banner">{bogoError}</div>}
          <div style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)', marginTop: '-0.2rem', marginBottom: '0.4rem' }}>
            Leave Free Item blank to auto-use Buy Item.
          </div>
          <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--theme-border)', borderRadius: '8px' }}>
            <div style={{ padding: '0.52rem 0.75rem', borderBottom: '1px solid var(--theme-border)', fontSize: '0.73rem', fontWeight: 700, color: '#475569', background: 'var(--theme-bg-light)', display: 'grid', gridTemplateColumns: '1.25fr 1.15fr 1.2fr', gap: '0.4rem' }}>
              <div>Rule / Window</div>
              <div>Buy / Free</div>
              <div style={{ textAlign: 'right' }}>Status / Actions</div>
            </div>
            {!bogoRules.length ? (
              <div style={{ padding: '0.65rem 0.75rem', color: 'var(--theme-text-muted)', fontSize: '0.78rem' }}>No BOGO rules found</div>
            ) : bogoRules.map((row) => (
              <div key={row.id} style={{ padding: '0.58rem 0.75rem', borderBottom: '1px solid var(--theme-border-light)', fontSize: '0.78rem', color: '#334155', display: 'grid', gridTemplateColumns: '1.25fr 1.15fr 1.2fr', gap: '0.4rem' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{row.rule_name || `BOGO #${row.id}`}</div>
                  <div style={{ color: 'var(--theme-text-secondary)' }}>{dayLabel(row.day_of_week)} · {bogoTimeWindow(row)}</div>
                  <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.7rem' }}>Created: {formatTimestamp(row.created_at)} by {formatUserLabel(row.created_by_username, row.created_by)}</div>
                  <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.7rem' }}>Modified: {formatTimestamp(row.updated_at || row.created_at)} by {formatUserLabel(row.updated_by_username, row.updated_by)}</div>
                </div>
                <div style={{ color: '#334155' }}>
                  Buy {Number(row.buy_qty || 0)} × {row.buy_menu_item_name || `Item #${row.buy_menu_item_id}`}
                  <br />
                  Free {Number(row.free_qty || 0)} × {row.free_menu_item_name || `Item #${row.free_menu_item_id}`}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', justifyContent: 'flex-end' }}>
                  <span style={{ color: row.is_active === false ? '#b91c1c' : '#15803d', fontWeight: 600 }}>{row.is_active === false ? 'INACTIVE' : 'ACTIVE'}</span>
                  <button type="button" onClick={() => startEditBogo(row)} style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#1e40af', borderRadius: '7px', padding: '0.26rem 0.5rem', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                  <button
                    type="button"
                    onClick={() => updateBogoStatus(row, row.is_active === false)}
                    style={{ border: '1px solid #cbd5e1', background: '#fff', color: row.is_active === false ? '#15803d' : '#b91c1c', borderRadius: '7px', padding: '0.26rem 0.5rem', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {row.is_active === false ? 'Activate' : 'Deactivate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BarPromotionsMaster;
